import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReportReason } from '@anidex/shared/economy';
import type {
  ActivityDTO,
  AtlasCardDTO,
  FriendDTO,
  FriendRequestDTO,
  Page,
  PlayerSummaryDTO,
  RedeemReferralResultDTO,
  ReferralDTO,
} from '@anidex/shared/types';
import { CARD_PAGE_SIZE, pageCursor } from '../../hooks/useListPage';
import { api, queryString } from '../api';
import { hasListIntent } from '../list-intent';
import { invalidateSocial, refreshProgress } from './cache';
import type { AtlasFilters } from './filters';
import { keys } from './keys';
import { cursorPagination } from './pagination';

export function useFriends() {
  return useQuery({
    queryKey: keys.friends,
    queryFn: () => api<{ items: FriendDTO[] }>('/friends'),
  });
}

export function useReferral() {
  return useQuery({ queryKey: keys.referral, queryFn: () => api<ReferralDTO>('/referrals') });
}

export function useRedeemReferral() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api<RedeemReferralResultDTO>('/referrals/redeem', { body: { code } }),
    onSuccess: (result) => {
      client.setQueryData(keys.referral, result.referral);
      refreshProgress(client, result.balance);
    },
  });
}

export function useAtlas(filters: AtlasFilters) {
  const page = filters.page ?? 0;
  const limit = filters.limit ?? CARD_PAGE_SIZE;
  const intent = hasListIntent(filters);
  return useQuery({
    queryKey: keys.atlas(filters),
    enabled: intent,
    queryFn: () =>
      api<Page<AtlasCardDTO>>(
        `/friends/atlas${queryString({
          search: filters.search,
          rarity: filters.rarity,
          friendId: filters.friendId,
          favorites: filters.favorites ? '1' : undefined,
          sort: filters.sort,
          limit,
          cursor: filters.cursor ?? pageCursor(page, limit),
        })}`,
      ),
    placeholderData: intent ? keepPreviousData : undefined,
  });
}

export function useFriendRequests() {
  return useQuery({
    queryKey: keys.friendRequests,
    queryFn: () => api<{ items: FriendRequestDTO[] }>('/friends/requests'),
  });
}

export function useFriendActivity() {
  return useInfiniteQuery({
    queryKey: keys.friendActivity,
    queryFn: ({ pageParam }) => api<Page<ActivityDTO>>(`/friends/activity${queryString({ cursor: pageParam })}`),
    ...cursorPagination,
  });
}

export function useBlocks() {
  return useQuery({
    queryKey: keys.blocks,
    queryFn: () => api<{ items: Array<{ player: PlayerSummaryDTO; blockedAt: string }> }>('/blocks'),
  });
}

export function useUserSearch(q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: keys.userSearch(query),
    queryFn: () => api<{ items: PlayerSummaryDTO[] }>(`/users${queryString({ q: query, limit: 10 })}`),
    enabled: query.length >= 2,
  });
}

export function useSendFriendRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (username: string) =>
      api<{ status: 'pending' | 'accepted'; requestId: string }>('/friends/requests', { body: { username } }),
    onSuccess: () => invalidateSocial(client),
  });
}

export function useAcceptFriendRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => api<{ status: 'accepted' }>(`/friends/requests/${requestId}/accept`, { body: {} }),
    onSuccess: () => invalidateSocial(client),
  });
}

export function useDeclineFriendRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => api<{ status: 'declined' }>(`/friends/requests/${requestId}/decline`, { body: {} }),
    onSuccess: () => invalidateSocial(client),
  });
}

export function useCancelFriendRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) =>
      api<{ status: 'cancelled' | 'declined' }>(`/friends/requests/${requestId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateSocial(client),
  });
}

export function useRemoveFriend() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<{ ok: boolean }>(`/friends/${userId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateSocial(client),
  });
}

export function useToggleFriendFavorite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, favorite }: { userId: string; favorite: boolean }) =>
      api<{ isFavorite: boolean }>(`/friends/${userId}/favorite`, { method: 'PUT', body: { favorite } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.friends }),
  });
}

export function useBlockUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<{ ok: boolean }>(`/blocks/${userId}`, { method: 'PUT', body: {} }),
    onSuccess: () => invalidateSocial(client),
  });
}

export function useUnblockUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<{ ok: boolean }>(`/blocks/${userId}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.blocks }),
  });
}

export function useReportUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      targetUserId: string;
      messageId?: string;
      reason: ReportReason;
      details?: string;
      alsoBlock?: boolean;
    }) => api<{ ok: boolean }>('/reports', { body: input }),
    onSuccess: (_result, input) => {
      if (input.alsoBlock) invalidateSocial(client);
    },
  });
}
